// Native (Capacitor) bridge. Every export is a no-op on the web, and plugins are lazy-loaded only inside the app.
import api from "@/lib/api";

export const isNative = () => typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
export const platform = () => (isNative() ? window.Capacitor.getPlatform() : "web");
// Public web address for links people open outside the app (the app's own origin is a local bundle).
export const publicOrigin = () => (isNative() ? process.env.REACT_APP_BACKEND_URL : window.location.origin);
export const NATIVE_SCHEME = "com.mylegalis.app";

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1]);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

// Save a generated file: browser download on the web, share sheet (save to Files/Drive, open, send) in the app.
export async function saveFile(blob, filename) {
  if (!isNative()) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return;
  }
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([import("@capacitor/filesystem"), import("@capacitor/share")]);
  const { uri } = await Filesystem.writeFile({ path: filename, data: await blobToBase64(blob), directory: Directory.Cache });
  await Share.share({ title: filename, files: [uri] }).catch(() => {});
}

export async function haptic(style = "Light") {
  if (!isNative()) return;
  const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
  await Haptics.impact({ style: ImpactStyle[style] || ImpactStyle.Light }).catch(() => {});
}

async function setupChrome() {
  const [{ StatusBar, Style }, { SplashScreen }] = await Promise.all([import("@capacitor/status-bar"), import("@capacitor/splash-screen")]);
  await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
  if (platform() === "android") await StatusBar.setBackgroundColor({ color: "#060607" }).catch(() => {});
  await SplashScreen.hide().catch(() => {});
}

async function setupDeepLinks(navigate) {
  const { App } = await import("@capacitor/app");
  const open = async ({ url }) => {
    const u = new URL(url);
    if (u.protocol === `${NATIVE_SCHEME}:`) {
      // Google sign-in finished in the browser; hand the one-time session to the normal callback.
      const { Browser } = await import("@capacitor/browser");
      await Browser.close().catch(() => {});
      const sid = u.searchParams.get("session_id");
      if (sid) navigate(`/app#session_id=${encodeURIComponent(sid)}`);
      return;
    }
    navigate(`${u.pathname}${u.search}${u.hash}`);
  };
  App.addListener("appUrlOpen", open);
  // Cold start: Android may have killed the app while the sign-in browser was open.
  const launch = await App.getLaunchUrl().catch(() => null);
  if (launch?.url) await open(launch);
  App.addListener("backButton", ({ canGoBack }) => (canGoBack ? window.history.back() : App.exitApp()));
}

async function setupPush(navigate) {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return;
  PushNotifications.addListener("registration", ({ value }) => api.post("/devices/register", { token: value, platform: platform() }).catch(() => {}));
  PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
    const link = notification?.data?.link;
    if (link) navigate(link);
  });
  await PushNotifications.register();
}

// Biometric app lock (Face ID / Touch ID / Android biometrics), opt-in from Profile.
export async function biometricAvailable() {
  if (!isNative()) return false;
  const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
  return (await BiometricAuth.checkBiometry()).isAvailable;
}

export async function getBiometricLock() {
  if (!isNative()) return false;
  const { Preferences } = await import("@capacitor/preferences");
  return (await Preferences.get({ key: "legalis-biometric" })).value === "1";
}

export async function setBiometricLock(on) {
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.set({ key: "legalis-biometric", value: on ? "1" : "0" });
}

async function unlock() {
  if (!(await getBiometricLock())) return true;
  const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
  try {
    await BiometricAuth.authenticate({ reason: "Unlock Legalis AI", cancelTitle: "Cancel", allowDeviceCredential: true });
    return true;
  } catch { return false; }
}

async function setupLock(onLock) {
  const { App } = await import("@capacitor/app");
  const guard = async () => { while (!(await unlock())) onLock?.(); };
  App.addListener("resume", guard);
  await guard();
}

export async function openExternal(url) {
  if (!isNative()) return window.open(url, "_blank", "noopener");
  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url });
}

let started = false;
export async function initNative({ navigate, loggedIn }) {
  if (!isNative() || started) return;
  started = true;
  document.documentElement.classList.add("native-app", `native-${platform()}`);
  await setupChrome();
  await setupDeepLinks(navigate);
  if (loggedIn) {
    await setupLock();
    // Push needs Firebase (google-services.json) in the build; registering without it crashes Android.
    if (process.env.REACT_APP_PUSH === "1") await setupPush(navigate).catch(() => {});
  }
}
